import React, { useState, useEffect, useCallback, useRef } from 'react';
import { SIGNERS } from '../config/company';
import { corporateDocsService } from '../services/api';
import { fmtDate } from '../utils/formatters';
import CascaDeModal from './shared/CascaDeModal';
import { DocumentViewerModal, DocViewerInfoItem, DocViewerEvent } from './shared/DocumentViewerModal';
import { useConfirmarPerigo } from './shared/ConfirmDialog';
import { useAviso } from './shared/Aviso';
import { campo, useEstadoDaCarga, EstadoDaLista, A_PROPRIA_LISTA_DIZ_O_VAZIO, textos, mensagemDoErroDeCarga } from '../design';
import { TONE_TEXT, TONE_BADGE } from '../utils/statusColors';
import { SortHeader } from './shared/SortableTableHeader';
import { podeAprovar, porqueNaoNovaVersao, avisoDaNovaVersao, consequenciaDaTransicao, porqueNaoExcluir, descricaoDaExclusao, avisoDaEdicao, confirmacaoDeCancelarAssinatura } from '../utils/docsSocietarios';

const CATEGORIES = [
  { value: 'Acordo de Quotistas', label: 'Acordo de Quotistas' },
  { value: 'Contrato Social', label: 'Contrato Social' },
  { value: 'Ata de Reuniao', label: 'Ata de Reuniao' },
  { value: 'Procuracao', label: 'Procuracao' },
  { value: 'Alteracao Contratual', label: 'Alteracao Contratual' },
  { value: 'Outro', label: 'Outro' },
];

// Cor de status = tom do domínio (utils/statusColors), não cor escolhida à mão (10/10/2026). Minuta é rascunho
// (neutro, era amarelo de alerta); Assinado é concluído (era o roxo); o segundo token segue sendo o texto dos cartões.
const STATUSES = [
  { value: 'Minuta', label: 'Minuta', color: TONE_BADGE.neutral },
  { value: 'Aprovado', label: 'Aprovado', color: TONE_BADGE.progress },
  { value: 'Aguardando Assinatura', label: 'Aguardando Assinatura', color: TONE_BADGE.warning },
  { value: 'Assinado', label: 'Assinado', color: TONE_BADGE.success },
  { value: 'Vigente', label: 'Vigente', color: TONE_BADGE.open },
  { value: 'Revogado', label: 'Revogado', color: TONE_BADGE.danger },
];

const STATUS_TRANSITIONS: Record<string, string[]> = {
  'Aprovado': ['Assinado', 'Revogado'],
  'Aguardando Assinatura': ['Aprovado', 'Revogado'],
  'Assinado': ['Vigente', 'Revogado'],
  'Vigente': ['Revogado'],
  'Minuta': ['Revogado'],
};

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};


const getFileIcon = (mimeType: string) => {
  if (mimeType?.startsWith('image/')) return '🖼️';
  if (mimeType === 'application/pdf') return '📄';
  if (mimeType?.includes('word')) return '📝';
  return '📁';
};

const Icon = ({ name, className = 'w-5 h-5' }: { name: string; className?: string }) => {
  const icons: any = {
    upload: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12' /></svg>,
    download: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4' /></svg>,
    eye: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' /><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' /></svg>,
    edit: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' /></svg>,
    trash: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' /></svg>,
    search: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' /></svg>,
    plus: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4v16m8-8H4' /></svg>,
    x: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' /></svg>,
    refresh: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' /></svg>,
    check: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' /></svg>,
    clock: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' /></svg>,
    send: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5' /></svg>,
    minus: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M20 12H4' /></svg>,
  };
  return icons[name] || null;
};

export default function CorporateDocsTab({ user }: { user?: any }) {
  const confirmarPerigo = useConfirmarPerigo();
  // D7 (10/10/2026): os erros saíam em alert(), a caixa nativa do navegador; agora no aviso do HUB.
  const avisar = useAviso();
  const erro = (e: any, padrao: string) => avisar(e?.response?.data?.message || `${padrao}: ${mensagemDoErroDeCarga(e)}`, 'error');
  const [docs, setDocs] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('Todos');
  const [statusFilter, setStatusFilter] = useState('Todos');
  const [sortCol, setSortCol] = useState('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Upload modal
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadForm, setUploadForm] = useState({ title: '', description: '', category: 'Outro', notes: '', effectiveDate: '', expirationDate: '' });
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState<any>(null);
  const [editForm, setEditForm] = useState({ title: '', description: '', category: '', notes: '', effectiveDate: '', expirationDate: '' });
  const [saving, setSaving] = useState(false);

  // Detail modal
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailDoc, setDetailDoc] = useState<any>(null);
  // URL assinada do arquivo, para o preview dentro do modal. Vem do mesmo endpoint do
  // download (`/corporate-docs/:id/download`), que devolve link do S3 válido por 15 min.
  const [detailUrl, setDetailUrl] = useState<string | null>(null);
  const [loadingDetailUrl, setLoadingDetailUrl] = useState(false);
  const [timeline, setTimeline] = useState<DocViewerEvent[]>([]);
  const [timelineLoading, setTimelineLoading] = useState(false);

  // Version upload modal
  const [versionModalOpen, setVersionModalOpen] = useState(false);
  const [versionDoc, setVersionDoc] = useState<any>(null);
  const [versionFile, setVersionFile] = useState<File | null>(null);
  const [versionDragOver, setVersionDragOver] = useState(false);
  const [uploadingVersion, setUploadingVersion] = useState(false);
  const versionFileInputRef = useRef<HTMLInputElement>(null);

  // Status change modal
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusDoc, setStatusDoc] = useState<any>(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [changingStatus, setChangingStatus] = useState(false);

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<any>(null);

  // Signature modal
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [signatureDoc, setSignatureDoc] = useState<any>(null);
  const [signers, setSigners] = useState<{ name: string; email: string; cpf: string; phone: string; role: string; authType: string }[]>([]);
  const [sendingForSignature, setSendingForSignature] = useState(false);

  // Signature status
  const [signatureStatusData, setSignatureStatusData] = useState<any>(null);
  // Só o setter interessa: o painel do modal cai para `detailDoc.signers` enquanto o status
  // ao vivo do ClickSign não chega, então não existe estado de "carregando" visível.
  const [, setLoadingSignatureStatus] = useState(false);
  const [statusAoVivoErro, setStatusAoVivoErro] = useState<string | null>(null);
  const [previaErro, setPreviaErro] = useState<string | null>(null);
  const [historicoErro, setHistoricoErro] = useState<string | null>(null);
  const signaturePollingRef = useRef<any>(null);

  // D1 (10/10/2026): a carga calava. Com a API fora a tela dizia "Nenhum documento encontrado", os cartões 0, e
  // "Novo Documento" seguia ativo: o operador subia de novo o contrato social, com outro código.
  const carga = useEstadoDaCarga();
  const rodarCarga = carga.rodar;
  const loadData = useCallback(async () => {
    await rodarCarga(async () => {
      const data = await corporateDocsService.getAll();
      setDocs(Array.isArray(data) ? data : []);
    });
  }, [rodarCarga]);
  const temFonte = carga.atualizadoEm !== null;

  useEffect(() => { loadData(); }, [loadData]);

  // Filter and sort
  const filteredDocs = docs
    .filter(d => {
      if (categoryFilter !== 'Todos' && d.category !== categoryFilter) return false;
      if (statusFilter !== 'Todos' && d.status !== statusFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        return (
          d.title?.toLowerCase().includes(s) ||
          d.code?.toLowerCase().includes(s) ||
          d.description?.toLowerCase().includes(s) ||
          d.fileName?.toLowerCase().includes(s)
        );
      }
      return true;
    })
    .sort((a, b) => {
      let valA = a[sortCol];
      let valB = b[sortCol];
      if (sortCol === 'fileSize' || sortCol === 'version') {
        valA = Number(valA) || 0;
        valB = Number(valB) || 0;
        return sortDir === 'asc' ? valA - valB : valB - valA;
      }
      if (sortCol === 'createdAt' || sortCol === 'updatedAt' || sortCol === 'effectiveDate') {
        valA = valA ? new Date(valA).getTime() : 0;
        valB = valB ? new Date(valB).getTime() : 0;
        return sortDir === 'asc' ? valA - valB : valB - valA;
      }
      const strA = String(valA || '').toLowerCase();
      const strB = String(valB || '').toLowerCase();
      if (strA < strB) return sortDir === 'asc' ? -1 : 1;
      if (strA > strB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const toggleSort = (col: string) => {
    if (sortCol === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  // Upload handlers
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      setUploadFile(file);
      if (!uploadForm.title) {
        setUploadForm(f => ({ ...f, title: file.name.replace(/\.[^/.]+$/, '') }));
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadFile(file);
      if (!uploadForm.title) {
        setUploadForm(f => ({ ...f, title: file.name.replace(/\.[^/.]+$/, '') }));
      }
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadForm.title) return;
    setUploading(true);
    try {
      await corporateDocsService.upload(uploadFile, {
        title: uploadForm.title,
        description: uploadForm.description || undefined,
        category: uploadForm.category,
        notes: uploadForm.notes || undefined,
        effectiveDate: uploadForm.effectiveDate || undefined,
        expirationDate: uploadForm.expirationDate || undefined,
      });
      setUploadModalOpen(false);
      setUploadFile(null);
      setUploadForm({ title: '', description: '', category: 'Outro', notes: '', effectiveDate: '', expirationDate: '' });
      loadData();
    } catch (e: any) {
      erro(e, 'Erro ao fazer upload');
    }
    setUploading(false);
  };

  // Edit handlers
  const openEditModal = (doc: any) => {
    setEditingDoc(doc);
    setEditForm({
      title: doc.title || '',
      description: doc.description || '',
      category: doc.category || 'Outro',
      notes: doc.notes || '',
      effectiveDate: doc.effectiveDate ? doc.effectiveDate.split('T')[0] : '',
      expirationDate: doc.expirationDate ? doc.expirationDate.split('T')[0] : '',
    });
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingDoc) return;
    setSaving(true);
    try {
      await corporateDocsService.update(editingDoc.id, {
        title: editForm.title,
        description: editForm.description || undefined,
        category: editForm.category,
        notes: editForm.notes || undefined,
        effectiveDate: editForm.effectiveDate || undefined,
        expirationDate: editForm.expirationDate || undefined,
      });
      setEditModalOpen(false);
      setEditingDoc(null);
      loadData();
    } catch (e: any) {
      erro(e, 'Erro ao salvar');
    }
    setSaving(false);
  };

  // Version upload
  const openVersionModal = (doc: any) => {
    setVersionDoc(doc);
    setVersionFile(null);
    setVersionModalOpen(true);
  };

  const handleVersionFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setVersionFile(file);
  };

  const handleUploadVersion = async () => {
    if (!versionDoc || !versionFile || porqueNaoNovaVersao(versionDoc)) return;
    setUploadingVersion(true);
    try {
      await corporateDocsService.uploadVersion(versionDoc.id, versionFile);
      setVersionModalOpen(false);
      setVersionDoc(null);
      setVersionFile(null);
      loadData();
    } catch (e: any) {
      erro(e, 'Erro ao enviar nova versao');
    }
    setUploadingVersion(false);
  };

  // Approve handler
  const handleApprove = async (doc: any) => {
    try {
      await corporateDocsService.approve(doc.id);
      loadData();
      if (detailDoc?.id === doc.id) {
        const updated = await corporateDocsService.getById(doc.id);
        setDetailDoc(updated);
      }
    } catch (e: any) {
      erro(e, 'Erro ao aprovar');
    }
  };

  // Status change
  const openStatusModal = (doc: any) => {
    setStatusDoc(doc);
    const transitions = STATUS_TRANSITIONS[doc.status] || [];
    setNewStatus(transitions[0] || '');
    setStatusNotes('');
    setStatusModalOpen(true);
  };

  const handleChangeStatus = async () => {
    if (!statusDoc || !newStatus || consequenciaDaTransicao(statusDoc.status, newStatus).travado) return;
    setChangingStatus(true);
    try {
      await corporateDocsService.changeStatus(statusDoc.id, { status: newStatus, notes: statusNotes || undefined });
      setStatusModalOpen(false);
      setStatusDoc(null);
      loadData();
    } catch (e: any) {
      erro(e, 'Erro ao mudar status');
    }
    setChangingStatus(false);
  };

  // Download handler
  const handleDownload = async (doc: any) => {
    try {
      await corporateDocsService.download(doc.id);
    } catch (e) {
      // Calava: o clique não fazia nada.
      erro(e, 'Não consegui baixar o arquivo');
    }
  };

  // Delete handler
  const handleDelete = async () => {
    if (!deleteConfirm || porqueNaoExcluir(deleteConfirm)) return;
    try {
      await corporateDocsService.delete(deleteConfirm.id);
      setDeleteConfirm(null);
      loadData();
    } catch (e: any) {
      erro(e, 'Erro ao excluir');
    }
  };

  // Signature handlers
  // Tudo de config/company.ts: o e-mail do Fábio estava escrito à mão aqui como
  // fabio@ipcom.com.br, divergindo do adm@ipcom.com.br que a aprovação dual do
  // backend usa para reconhecer o sócio (corporate-docs.service).
  const DEFAULT_SIGNERS = [
    { name: SIGNERS.fabio.name, email: SIGNERS.fabio.email, cpf: SIGNERS.fabio.cpf, phone: SIGNERS.fabio.phone || '', role: 'sign', authType: 'email' },
    { name: SIGNERS.heitor.name, email: SIGNERS.heitor.email, cpf: SIGNERS.heitor.cpf, phone: SIGNERS.heitor.phone || '', role: 'sign', authType: 'email' },
  ];

  const openSignatureModal = (doc: any) => {
    setSignatureDoc(doc);
    setSigners([...DEFAULT_SIGNERS]);
    setSignatureModalOpen(true);
  };

  const addSigner = () => {
    setSigners(prev => [...prev, { name: '', email: '', cpf: '', phone: '', role: 'witness', authType: 'email' }]);
  };

  const removeSigner = (index: number) => {
    setSigners(prev => prev.filter((_, i) => i !== index));
  };

  const updateSigner = (index: number, field: string, value: string) => {
    setSigners(prev => prev.map((s, i) => i === index ? { ...s, [field]: value } : s));
  };

  const handleSendForSignature = async () => {
    if (!signatureDoc || signers.length === 0) return;
    const invalidSigners = signers.filter(s => !s.name || !s.email || !s.cpf);
    if (invalidSigners.length > 0) {
      avisar('Preencha nome, email e CPF de todos os signatarios', 'error');
      return;
    }
    const missingPhone = signers.filter(s => s.authType === 'whatsapp' && !s.phone);
    if (missingPhone.length > 0) {
      avisar('Preencha o telefone dos signatarios com autenticacao por WhatsApp', 'error');
      return;
    }
    setSendingForSignature(true);
    try {
      await corporateDocsService.sendForSignature(signatureDoc.id, signers);
      setSignatureModalOpen(false);
      setSignatureDoc(null);
      loadData();
    } catch (e: any) {
      erro(e, 'Erro ao enviar para assinatura');
    }
    setSendingForSignature(false);
  };

  const loadSignatureStatus = async (docId: number) => {
    setLoadingSignatureStatus(true);
    try {
      const data = await corporateDocsService.getSignatureStatus(docId);
      setSignatureStatusData(data);
      setStatusAoVivoErro(null);
    } catch (e) {
      // O painel cai na foto do cadastro; agora diz que é a foto, e não o ClickSign.
      setStatusAoVivoErro(mensagemDoErroDeCarga(e));
    }
    setLoadingSignatureStatus(false);
  };

  const handleCancelSignature = async (doc: any) => {
    if (!await confirmarPerigo(confirmacaoDeCancelarAssinatura(doc))) return;
    try {
      await corporateDocsService.cancelSignature(doc.id);
      loadData();
      if (detailDoc?.id === doc.id) {
        const updated = await corporateDocsService.getById(doc.id);
        setDetailDoc(updated);
        setSignatureStatusData(null);
      }
    } catch (e: any) {
      erro(e, 'Erro ao cancelar assinatura');
    }
  };

  const handleDownloadSignedPdf = async (doc: any) => {
    try {
      await corporateDocsService.downloadSignedPdf(doc.id);
    } catch (e) {
      erro(e, 'Não consegui baixar o PDF assinado');
    }
  };

  // Detail modal
  const openDetail = async (doc: any) => {
    try {
      const full = await corporateDocsService.getById(doc.id);
      setDetailDoc(full);
      setDetailModalOpen(true);
      setSignatureStatusData(null);
      setDetailUrl(null);
      setTimeline([]);
      setStatusAoVivoErro(null);
      setPreviaErro(null);
      setHistoricoErro(null);
      if (full.clicksignDocumentKey) {
        loadSignatureStatus(full.id);
      }
      loadDetailUrl(full);
      loadTimeline(full.id);
    } catch (e) {
      // Calava: o olho não abria nada.
      erro(e, 'Não consegui abrir o documento');
    }
  };

  /** Documento assinado mostra o PDF ASSINADO; o resto mostra o arquivo original. */
  const loadDetailUrl = async (doc: any) => {
    setLoadingDetailUrl(true);
    try {
      const assinado = doc.status === 'Assinado' && doc.signedPdfS3Key;
      const { url } = assinado
        ? await corporateDocsService.getSignedPdfUrl(doc.id)
        : await corporateDocsService.getDownloadUrl(doc.id);
      setDetailUrl(url);
      setPreviaErro(null);
    } catch (e) {
      setDetailUrl(null);
      setPreviaErro(mensagemDoErroDeCarga(e));
    } finally {
      setLoadingDetailUrl(false);
    }
  };

  const loadTimeline = async (id: number) => {
    setTimelineLoading(true);
    try {
      setTimeline(await corporateDocsService.getHistory(id));
      setHistoricoErro(null);
    } catch (e) {
      // Acessório: falhar aqui não pode esconder o documento, mas tem de dizer (era "sem histórico").
      setTimeline([]);
      setHistoricoErro(mensagemDoErroDeCarga(e));
    } finally {
      setTimelineLoading(false);
    }
  };

  /** Painel lateral do modal do olho. */
  const infoDoDocumento = (doc: any): DocViewerInfoItem[] => {
    const itens: DocViewerInfoItem[] = [
      {
        label: 'Status',
        value: doc.status,
        tone: doc.status === 'Assinado' ? 'success'
          : doc.status === 'Aguardando Assinatura' ? 'warning'
          : doc.status === 'Arquivado' ? 'muted'
          : 'default',
      },
      { label: 'Categoria', value: doc.category },
      { label: 'Versão', value: 'v' + doc.version },
      { label: 'Arquivo', value: doc.fileName },
      { label: 'Tamanho', value: formatFileSize(Number(doc.fileSize)) },
    ];
    if (doc.effectiveDate) itens.push({ label: 'Vigência', value: fmtDate(doc.effectiveDate) });
    if (doc.expirationDate) itens.push({ label: 'Expiração', value: fmtDate(doc.expirationDate), tone: 'warning' });
    itens.push({ label: 'Enviado por', value: doc.uploadedByName || '—' });
    itens.push({ label: 'Criado em', value: fmtDate(doc.createdAt) });
    if (doc.signedAt) itens.push({ label: 'Assinado em', value: fmtDate(doc.signedAt), tone: 'success' });
    return itens;
  };

  // Polling for signature status
  useEffect(() => {
    if (detailModalOpen && detailDoc?.status === 'Aguardando Assinatura' && detailDoc?.clicksignDocumentKey) {
      signaturePollingRef.current = setInterval(() => {
        loadSignatureStatus(detailDoc.id);
      }, 15000);
      return () => clearInterval(signaturePollingRef.current);
    }
    return () => {
      if (signaturePollingRef.current) clearInterval(signaturePollingRef.current);
    };
  }, [detailModalOpen, detailDoc?.id, detailDoc?.status]);

  // D6 (10/10/2026): o sócio é o e-mail da conta, o mesmo critério do servidor (utils/docsSocietarios).
  const canApprove = (doc: any) => podeAprovar(doc, user);

  const ordem = { col: sortCol, dir: sortDir };
  const Ordenavel = ({ col, label }: { col: string; label: string }) => (
    <SortHeader col={col} label={label} sort={ordem} onToggle={toggleSort} className="px-4 py-3 text-xs font-semibold uppercase" />
  );

  if (carga.estado === 'carregando' && !temFonte) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-400">Carregando documentos...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Documentos Societarios</h2>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {STATUSES.map(s => {
          const count = docs.filter(d => d.status === s.value).length;
          return (
            <div key={s.value} className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
              <p className="text-slate-400 text-sm">{s.label}</p>
              <p className={`text-2xl font-bold ${temFonte ? s.color.split(' ')[1] : 'text-slate-500'}`} data-kpi={s.value}>{temFonte ? count : textos.numeroSemFonte}</p>
            </div>
          );
        })}
      </div>

      {/* Actions Bar */}
      <div className="flex flex-wrap items-center gap-4">
        {carga.estado === 'pronto' && <button
          onClick={() => setUploadModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium"
        >
          <Icon name="upload" className="w-5 h-5" />
          Novo Documento
        </button>}

        <div className="flex-1" />

        <div className="relative">
          <Icon name="search" className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className={`pl-10 pr-4 py-2 rounded-lg placeholder-slate-500 focus:outline-none w-64 ${campo}`}
          />
        </div>

        <select
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
          className={`px-3 py-2 ${campo} rounded-lg focus:outline-none`}
        >
          <option value="Todos">Todas Categorias</option>
          {CATEGORIES.map(c => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className={`px-3 py-2 ${campo} rounded-lg focus:outline-none`}
        >
          <option value="Todos">Todos Status</option>
          {STATUSES.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>

        <button
          onClick={loadData}
          className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg"
          title="Atualizar"
        >
          <Icon name="refresh" className="w-5 h-5" />
        </button>
      </div>

      {/* Documents Table */}
      <EstadoDaLista carga={carga} temDados={temFonte} oQue="os documentos societários" onTentarDeNovo={loadData} vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}>
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-900/50">
              <tr>
                <Ordenavel col="code" label="Codigo" />
                <Ordenavel col="title" label="Titulo" />
                <Ordenavel col="category" label="Categoria" />
                <Ordenavel col="status" label="Status" />
                <th className="px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Aprovacao</th>
                <Ordenavel col="version" label="Versao" />
                <Ordenavel col="createdAt" label="Criado" />
                <th className="px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Acoes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {filteredDocs.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                    Nenhum documento encontrado
                  </td>
                </tr>
              ) : (
                filteredDocs.map(doc => {
                  const statusInfo = STATUSES.find(s => s.value === doc.status);
                  return (
                    <tr key={doc.id} className="hover:bg-slate-700/30">
                      <td className="px-4 py-3 text-slate-300 font-mono text-sm">{doc.code}</td>
                      <td className="px-4 py-3">
                        <div className="text-white font-medium">{doc.title}</div>
                        {doc.description && (
                          <div className="text-slate-500 text-sm truncate max-w-xs">{doc.description}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-300">{doc.category}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo?.color || ''}`}>
                          {doc.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm ${doc.approvedByFabio ? 'text-green-400' : 'text-slate-600'}`} title={doc.approvedByFabio ? `Aprovado em ${fmtDate(doc.approvedByFabioDate)}` : 'Pendente'}>
                            {doc.approvedByFabio ? '✓' : '○'} Fabio
                          </span>
                          <span className={`text-sm ${doc.approvedByHeitor ? 'text-green-400' : 'text-slate-600'}`} title={doc.approvedByHeitor ? `Aprovado em ${fmtDate(doc.approvedByHeitorDate)}` : 'Pendente'}>
                            {doc.approvedByHeitor ? '✓' : '○'} Heitor
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-400 text-center">v{doc.version}</td>
                      <td className="px-4 py-3 text-slate-400 text-sm">{fmtDate(doc.createdAt)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => openDetail(doc)}
                            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-600 rounded"
                            title="Ver Detalhes"
                          >
                            <Icon name="eye" className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDownload(doc)}
                            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-600 rounded"
                            title="Download"
                          >
                            <Icon name="download" className="w-4 h-4" />
                          </button>
                          {canApprove(doc) && (
                            <button
                              onClick={() => handleApprove(doc)}
                              className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-600 rounded"
                              title="Aprovar"
                            >
                              <Icon name="check" className="w-4 h-4" />
                            </button>
                          )}
                          {doc.status === 'Aprovado' && (
                            <button
                              onClick={() => openSignatureModal(doc)}
                              className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-600 rounded"
                              title="Enviar para Assinatura"
                            >
                              <Icon name="send" className="w-4 h-4" />
                            </button>
                          )}
                          {doc.status === 'Assinado' && doc.signedPdfS3Key && (
                            <button
                              onClick={() => handleDownloadSignedPdf(doc)}
                              className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-600 rounded"
                              title="Download PDF Assinado"
                            >
                              <Icon name="download" className="w-4 h-4" />
                            </button>
                          )}
                          {(STATUS_TRANSITIONS[doc.status]?.length || 0) > 0 && (
                            <button
                              onClick={() => openStatusModal(doc)}
                              className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-600 rounded"
                              title="Mudar Status"
                            >
                              <Icon name="clock" className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => openEditModal(doc)}
                            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-600 rounded"
                            title="Editar"
                          >
                            <Icon name="edit" className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openVersionModal(doc)}
                            disabled={!!porqueNaoNovaVersao(doc)}
                            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-600 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                            title={porqueNaoNovaVersao(doc) || 'Nova Versao'}
                          >
                            <Icon name="upload" className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(doc)}
                            className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-slate-600 rounded"
                            title="Excluir"
                          >
                            <Icon name="trash" className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      </EstadoDaLista>

      {/* ==================== UPLOAD MODAL ==================== */}
      {uploadModalOpen && (
        // Formulário: não fechava ao clicar fora e continua não fechando. O fundo
        // translúcido (bg-slate-800/50) dá lugar ao fundo sólido da casca.
        <CascaDeModal
          largura="sm"
          titulo={'Novo Documento Societario'}
          onClose={() => { setUploadModalOpen(false); setUploadFile(null); }}
          corpoClassName="p-4 space-y-4"
          rodape={
            <div className="ml-auto flex gap-3">
              <button onClick={() => { setUploadModalOpen(false); setUploadFile(null); }} className="px-4 py-2 text-slate-400 hover:text-white">Cancelar</button>
              <button
                onClick={handleUpload}
                disabled={!uploadFile || !uploadForm.title || uploading}
                className="px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? 'Enviando...' : 'Enviar Documento'}
              </button>
            </div>
          }
        >
              {/* File Drop Zone */}
              <div
                onDrop={handleDrop}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                  dragOver ? 'border-primary bg-primary/10' : uploadFile ? 'border-green-500/50 bg-green-500/5' : 'border-slate-600 hover:border-slate-500'
                }`}
              >
                {uploadFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-xl">{getFileIcon(uploadFile.type)}</span>
                    <span className="text-white">{uploadFile.name}</span>
                    <span className="text-slate-400 text-sm">({formatFileSize(uploadFile.size)})</span>
                  </div>
                ) : (
                  <>
                    <Icon name="upload" className="w-8 h-8 mx-auto text-slate-500 mb-2" />
                    <p className="text-slate-400">Arraste o arquivo aqui ou clique para selecionar</p>
                    <p className="text-slate-500 text-sm mt-1">PDF, DOC, DOCX, PNG, JPG (max 50MB)</p>
                  </>
                )}
                <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileSelect} accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1">Titulo *</label>
                <input
                  type="text"
                  value={uploadForm.title}
                  onChange={e => setUploadForm(f => ({ ...f, title: e.target.value }))}
                  className={`w-full px-3 py-2 ${campo} rounded-lg focus:outline-none`}
                />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1">Descricao</label>
                <textarea
                  value={uploadForm.description}
                  onChange={e => setUploadForm(f => ({ ...f, description: e.target.value }))}
                  rows={2}
                  className={`w-full px-3 py-2 ${campo} rounded-lg focus:outline-none`}
                />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1">Categoria</label>
                <select
                  value={uploadForm.category}
                  onChange={e => setUploadForm(f => ({ ...f, category: e.target.value }))}
                  className={`w-full px-3 py-2 ${campo} rounded-lg focus:outline-none`}
                >
                  {CATEGORIES.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Data de Vigencia</label>
                  <input
                    type="date"
                    value={uploadForm.effectiveDate}
                    onChange={e => setUploadForm(f => ({ ...f, effectiveDate: e.target.value }))}
                    className={`w-full px-3 py-2 ${campo} rounded-lg focus:outline-none`}
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Expiracao</label>
                  <input
                    type="date"
                    value={uploadForm.expirationDate}
                    onChange={e => setUploadForm(f => ({ ...f, expirationDate: e.target.value }))}
                    className={`w-full px-3 py-2 ${campo} rounded-lg focus:outline-none`}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1">Observacoes</label>
                <textarea
                  value={uploadForm.notes}
                  onChange={e => setUploadForm(f => ({ ...f, notes: e.target.value }))}
                  rows={2}
                  className={`w-full px-3 py-2 ${campo} rounded-lg focus:outline-none`}
                />
              </div>
        </CascaDeModal>
      )}

      {/* ==================== EDIT MODAL ==================== */}
      {editModalOpen && editingDoc && (
        // Formulário: não fecha ao clicar fora, como já era.
        <CascaDeModal
          largura="sm"
          titulo={'Editar Documento'}
          onClose={() => setEditModalOpen(false)}
          subtitulo={editingDoc.code}
          corpoClassName="p-4 space-y-4"
          rodape={
            <div className="ml-auto flex gap-3">
              <button onClick={() => setEditModalOpen(false)} className="px-4 py-2 text-slate-400 hover:text-white">Cancelar</button>
              <button
                onClick={handleSaveEdit}
                disabled={saving}
                className="px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium disabled:opacity-50"
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          }
        >
              {avisoDaEdicao(editingDoc) && (
                <p className={`text-xs ${TONE_TEXT.warning}`} data-testid="aviso-da-edicao">{avisoDaEdicao(editingDoc)}</p>
              )}
              <div>
                <label className="block text-sm text-slate-400 mb-1">Titulo</label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                  className={`w-full px-3 py-2 ${campo} rounded-lg focus:outline-none`}
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Descricao</label>
                <textarea
                  value={editForm.description}
                  onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                  rows={2}
                  className={`w-full px-3 py-2 ${campo} rounded-lg focus:outline-none`}
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Categoria</label>
                <select
                  value={editForm.category}
                  onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))}
                  className={`w-full px-3 py-2 ${campo} rounded-lg focus:outline-none`}
                >
                  {CATEGORIES.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Data de Vigencia</label>
                  <input
                    type="date"
                    value={editForm.effectiveDate}
                    onChange={e => setEditForm(f => ({ ...f, effectiveDate: e.target.value }))}
                    className={`w-full px-3 py-2 ${campo} rounded-lg focus:outline-none`}
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Expiracao</label>
                  <input
                    type="date"
                    value={editForm.expirationDate}
                    onChange={e => setEditForm(f => ({ ...f, expirationDate: e.target.value }))}
                    className={`w-full px-3 py-2 ${campo} rounded-lg focus:outline-none`}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Observacoes</label>
                <textarea
                  value={editForm.notes}
                  onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                  rows={2}
                  className={`w-full px-3 py-2 ${campo} rounded-lg focus:outline-none`}
                />
              </div>
        </CascaDeModal>
      )}

      {/* ==================== DETAIL MODAL ==================== */}
      {/* Componente compartilhado — ver shared/DocumentViewerModal.tsx. Antes desta tela ser
          migrada, o olho de Docs Societarios abria um max-w-2xl com METADADO e nenhum
          preview: era o unico dos submodulos administrativos em que nao dava para ver o
          documento sem baixar. */}
      {detailModalOpen && detailDoc && (
        <DocumentViewerModal
          title={detailDoc.code + ' - ' + detailDoc.title}
          subtitle={detailDoc.fileName}
          fileUrl={detailUrl}
          mimeType={detailDoc.mimeType}
          fileName={detailDoc.fileName}
          loading={loadingDetailUrl && !detailUrl}
          accent="primary"
          onClose={() => setDetailModalOpen(false)}
          info={infoDoDocumento(detailDoc)}
          signers={(signatureStatusData?.signers as any) || detailDoc.signers || []}
          documentSignedAt={detailDoc.signedAt}
          timeline={timeline}
          timelineLoading={timelineLoading}
          headerActions={
            <>
              {detailDoc.status === 'Assinado' && detailDoc.signedPdfS3Key && (
                <button
                  onClick={() => handleDownloadSignedPdf(detailDoc)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200"
                  title="Baixar o PDF assinado"
                >
                  <Icon name="download" className="w-4 h-4" />
                  PDF assinado
                </button>
              )}
              <button
                onClick={() => handleDownload(detailDoc)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200"
                title="Baixar o arquivo original"
              >
                <Icon name="download" className="w-4 h-4" />
                Baixar
              </button>
            </>
          }
          sidebarActions={
            detailDoc.status === 'Aprovado' ? (
              <button
                onClick={() => { setDetailModalOpen(false); openSignatureModal(detailDoc); }}
                className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-amber-600/20 hover:bg-amber-600/30 text-amber-300 border border-amber-500/30"
              >
                <Icon name="send" className="w-3 h-3" />
                Enviar para assinatura
              </button>
            ) : detailDoc.status === 'Aguardando Assinatura' ? (
              <button
                onClick={() => handleCancelSignature(detailDoc)}
                className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-red-600/20 hover:bg-red-600/30 text-red-300 border border-red-500/30"
              >
                <Icon name="x" className="w-3 h-3" />
                Cancelar assinatura
              </button>
            ) : null
          }
          sidebarExtra={
            <>
              {(statusAoVivoErro || previaErro || historicoErro) && (
                <div role="status" className={`text-xs space-y-1 ${TONE_TEXT.warning}`} data-testid="detalhe-cargas">
                  {previaErro && <p>Não consegui carregar a prévia do arquivo: {previaErro}.</p>}
                  {statusAoVivoErro && <p>Não consegui ler a assinatura no ClickSign ({statusAoVivoErro}): os signatários abaixo são os do cadastro, não o status ao vivo.</p>}
                  {historicoErro && <p>Não consegui carregar o histórico: {historicoErro}.</p>}
                </div>
              )}
              {/* Aprovacao dos socios: e o que esta tela tem de proprio, e o motivo dela
                  existir separada de Oficios. Fica no painel, nao no rodape. */}
              <div>
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Aprovação dos sócios</h4>
                <div className="space-y-2">
                  {[
                    { nome: 'Fabio', ok: detailDoc.approvedByFabio, data: detailDoc.approvedByFabioDate },
                    { nome: 'Heitor', ok: detailDoc.approvedByHeitor, data: detailDoc.approvedByHeitorDate },
                  ].map((socio) => (
                    <div key={socio.nome} className="flex items-center gap-2 text-sm">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${socio.ok ? 'bg-green-400' : 'bg-slate-600'}`} />
                      <span className="text-slate-200">{socio.nome}</span>
                      <span className={`ml-auto text-xs ${socio.ok ? 'text-green-400' : 'text-slate-500'}`}>
                        {socio.ok ? fmtDate(socio.data) : 'Pendente'}
                      </span>
                    </div>
                  ))}
                </div>
                {canApprove(detailDoc) && (
                  <button
                    onClick={() => handleApprove(detailDoc)}
                    className="mt-2 w-full flex items-center justify-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-green-600/20 hover:bg-green-600/30 text-green-300 border border-green-500/30"
                  >
                    <Icon name="check" className="w-3 h-3" />
                    Aprovar documento
                  </button>
                )}
              </div>

              {detailDoc.description && (
                <div>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Descrição</h4>
                  <p className="text-sm text-slate-300">{detailDoc.description}</p>
                </div>
              )}

              {detailDoc.notes && (
                <div>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Observações</h4>
                  <p className="text-sm text-slate-300">{detailDoc.notes}</p>
                </div>
              )}
            </>
          }
        />
      )}

      {/* ==================== VERSION MODAL ==================== */}
      {versionModalOpen && versionDoc && (
        // Arquivo escolhido: não fecha ao clicar fora, como já era. Só moldura: o
        // versionamento (v atual → v+1) e o handleUploadVersion não mudam.
        <CascaDeModal
          largura="xs"
          titulo={'Nova Versao'}
          onClose={() => setVersionModalOpen(false)}
          subtitulo={versionDoc.code}
          corpoClassName="p-4 space-y-4"
          rodape={
            <div className="ml-auto flex gap-3">
              <button onClick={() => setVersionModalOpen(false)} className="px-4 py-2 text-slate-400 hover:text-white">Cancelar</button>
              <button
                onClick={handleUploadVersion}
                disabled={!versionFile || uploadingVersion}
                className="px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploadingVersion ? 'Enviando...' : 'Enviar Nova Versao'}
              </button>
            </div>
          }
        >
              <p className="text-slate-400 text-sm">
                Versao atual: <span className="text-white">v{versionDoc.version}</span> → Nova versao: <span className="text-white font-semibold">v{versionDoc.version + 1}</span>
              </p>
              {avisoDaNovaVersao(versionDoc) && (
                <p className={`text-xs ${TONE_TEXT.warning}`} data-testid="aviso-nova-versao">{avisoDaNovaVersao(versionDoc)}</p>
              )}
              <div
                onClick={() => versionFileInputRef.current?.click()}
                onDrop={e => {
                  e.preventDefault();
                  setVersionDragOver(false);
                  const file = e.dataTransfer.files[0];
                  if (file) setVersionFile(file);
                }}
                onDragOver={e => { e.preventDefault(); setVersionDragOver(true); }}
                onDragLeave={() => setVersionDragOver(false)}
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                  versionDragOver ? 'border-primary bg-primary/10' : versionFile ? 'border-green-500/50 bg-green-500/5' : 'border-slate-600 hover:border-slate-500'
                }`}
              >
                {versionFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-xl">{getFileIcon(versionFile.type)}</span>
                    <span className="text-white">{versionFile.name}</span>
                    <span className="text-slate-400 text-sm">({formatFileSize(versionFile.size)})</span>
                  </div>
                ) : (
                  <>
                    <Icon name="upload" className="w-8 h-8 mx-auto text-slate-500 mb-2" />
                    <p className="text-slate-400">Clique para selecionar o arquivo</p>
                  </>
                )}
                <input ref={versionFileInputRef} type="file" className="hidden" onChange={handleVersionFileSelect} accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" />
              </div>
        </CascaDeModal>
      )}

      {/* ==================== STATUS CHANGE MODAL ==================== */}
      {statusModalOpen && statusDoc && (
        // Novo status escolhido: não fecha ao clicar fora, como já era. As transições
        // permitidas continuam vindo de STATUS_TRANSITIONS.
        <CascaDeModal
          largura="xs"
          titulo={'Alterar Status'}
          onClose={() => setStatusModalOpen(false)}
          subtitulo={statusDoc.code}
          corpoClassName="p-4 space-y-4"
          rodape={
            <div className="ml-auto flex gap-3">
              <button onClick={() => setStatusModalOpen(false)} className="px-4 py-2 text-slate-400 hover:text-white">Cancelar</button>
              <button
                onClick={handleChangeStatus}
                disabled={!newStatus || changingStatus || consequenciaDaTransicao(statusDoc.status, newStatus).travado}
                // perigo dinâmico: o botão parece a consequência da transição escolhida. Revogado é
                // terminal (STATUS_TRANSITIONS não tem saída dele), as outras seguem adiante.
                className={`px-4 py-2.5 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed ${newStatus === 'Revogado' ? 'bg-red-600 hover:bg-red-500' : 'bg-primary hover:bg-primary-strong'}`}
              >
                {changingStatus ? 'Alterando...' : 'Confirmar'}
              </button>
            </div>
          }
        >
              <p className="text-slate-400 text-sm">
                Status atual: <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUSES.find(s => s.value === statusDoc.status)?.color || ''}`}>{statusDoc.status}</span>
              </p>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Novo Status</label>
                <select
                  value={newStatus}
                  onChange={e => setNewStatus(e.target.value)}
                  className={`w-full px-3 py-2 ${campo} rounded-lg focus:outline-none`}
                >
                  {(STATUS_TRANSITIONS[statusDoc.status] || []).map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Observacao (opcional)</label>
                {consequenciaDaTransicao(statusDoc.status, newStatus).texto && (
                  <p className={`mt-2 text-xs ${consequenciaDaTransicao(statusDoc.status, newStatus).travado ? TONE_TEXT.warning : 'text-slate-400'}`} data-testid="consequencia-do-status">
                    {consequenciaDaTransicao(statusDoc.status, newStatus).texto}
                  </p>
                )}
              </div>
              <div>
                <textarea
                  value={statusNotes}
                  onChange={e => setStatusNotes(e.target.value)}
                  rows={2}
                  className={`w-full px-3 py-2 ${campo} rounded-lg focus:outline-none`}
                  placeholder="Motivo da alteracao..."
                />
              </div>
        </CascaDeModal>
      )}

      {/* ==================== DELETE CONFIRMATION ==================== */}
      {deleteConfirm && (
        // Confirmação de perigo: não fechava ao clicar fora e continua não fechando
        // (sem mudança de comportamento); ganha o ×. O ícone grande de lixeira sai
        // (§10.4: sem crachá de ícone); "Excluir" segue vermelho.
        <CascaDeModal
          largura="xs"
          titulo="Excluir Documento"
          subtitulo={deleteConfirm.code}
          onClose={() => setDeleteConfirm(null)}
          corpoClassName="px-5 py-4"
          rodape={
            <div className="ml-auto flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 text-slate-400 hover:text-white">Cancelar</button>
              {(deleteConfirm.status === 'Assinado' || deleteConfirm.status === 'Vigente') && (
                <button
                  onClick={() => { const d = deleteConfirm; setDeleteConfirm(null); openStatusModal(d); setNewStatus('Revogado'); }}
                  className="px-4 py-2.5 bg-slate-700 text-white rounded-lg hover:bg-slate-600 font-medium"
                >
                  Revogar em vez de excluir
                </button>
              )}
              <button
                onClick={handleDelete}
                disabled={!!porqueNaoExcluir(deleteConfirm)}
                title={porqueNaoExcluir(deleteConfirm) || undefined}
                className="px-4 py-2.5 bg-danger text-white rounded-lg hover:bg-danger-strong font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Excluir
              </button>
            </div>
          }
        >
              <p className="text-slate-400 text-sm mb-1">
                Deseja excluir <span className="text-white">{deleteConfirm.code} - {deleteConfirm.title}</span>?
              </p>
              <p className="text-slate-300 text-xs mb-1" data-testid="descricao-da-exclusao">{descricaoDaExclusao(deleteConfirm)}</p>
              {porqueNaoExcluir(deleteConfirm)
                ? <p className={`text-xs ${TONE_TEXT.warning}`} data-testid="porque-nao-excluir">{porqueNaoExcluir(deleteConfirm)}</p>
                : <p className="text-red-400 text-xs">Esta acao nao pode ser desfeita</p>}
        </CascaDeModal>
      )}
      {/* ==================== SIGNATURE MODAL ==================== */}
      {signatureModalOpen && signatureDoc && (
        // Signatários digitados: não fecha ao clicar fora, como já era. Envio para fora
        // (ClickSign): só moldura, handleSendForSignature não muda. A primária âmbar
        // vira o laranja (âmbar é tom de atenção, não de ação).
        <CascaDeModal
          largura="base"
          titulo={'Enviar para Assinatura'}
          onClose={() => setSignatureModalOpen(false)}
          subtitulo={signatureDoc.code}
          corpoClassName="p-4 space-y-4"
          rodape={
            <div className="ml-auto flex gap-3">
              <button onClick={() => setSignatureModalOpen(false)} className="px-4 py-2 text-slate-400 hover:text-white">Cancelar</button>
              <button
                onClick={handleSendForSignature}
                disabled={sendingForSignature || signers.length === 0}
                className="flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Icon name="send" className="w-4 h-4" />
                {sendingForSignature ? 'Enviando...' : `Enviar para ${signers.length} Signatario(s)`}
              </button>
            </div>
          }
        >
              <p className="text-slate-400 text-sm">
                Documento: <span className="text-white">{signatureDoc.title}</span> (v{signatureDoc.version})
              </p>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-slate-300 font-medium">Signatarios</p>
                  <button
                    onClick={addSigner}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600"
                  >
                    <Icon name="plus" className="w-4 h-4" /> Adicionar
                  </button>
                </div>

                {signers.map((signer, index) => (
                  <div key={index} className="bg-central-fundo/50 rounded-lg p-3 space-y-2 border border-central-borda">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400 text-xs font-medium">Signatario {index + 1}</span>
                      {signers.length > 1 && (
                        <button
                          onClick={() => removeSigner(index)}
                          className="text-slate-500 hover:text-red-400"
                        >
                          <Icon name="minus" className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        placeholder="Nome completo"
                        value={signer.name}
                        onChange={e => updateSigner(index, 'name', e.target.value)}
                        className={`px-3 py-2 ${campo} rounded-lg text-sm focus:outline-none`}
                      />
                      <input
                        type="email"
                        placeholder="Email"
                        value={signer.email}
                        onChange={e => updateSigner(index, 'email', e.target.value)}
                        className={`px-3 py-2 ${campo} rounded-lg text-sm focus:outline-none`}
                      />
                      <input
                        type="text"
                        placeholder="CPF (000.000.000-00)"
                        value={signer.cpf}
                        onChange={e => updateSigner(index, 'cpf', e.target.value)}
                        className={`px-3 py-2 ${campo} rounded-lg text-sm focus:outline-none`}
                      />
                      <div className="flex gap-2">
                        <select
                          value={signer.role}
                          onChange={e => updateSigner(index, 'role', e.target.value)}
                          className={`flex-1 px-3 py-2 ${campo} rounded-lg text-sm focus:outline-none`}
                        >
                          <option value="sign">Assinar</option>
                          <option value="witness">Testemunha</option>
                          <option value="approve">Aprovar</option>
                        </select>
                        <select
                          value={signer.authType}
                          onChange={e => updateSigner(index, 'authType', e.target.value)}
                          className={`flex-1 px-3 py-2 ${campo} rounded-lg text-sm focus:outline-none`}
                        >
                          <option value="email">Email</option>
                          <option value="whatsapp">WhatsApp</option>
                          <option value="icp_brasil">ICP-Brasil</option>
                        </select>
                      </div>
                      {signer.authType === 'whatsapp' && (
                        <input
                          type="text"
                          placeholder="Telefone (ex: 5545999999999)"
                          value={signer.phone}
                          onChange={e => updateSigner(index, 'phone', e.target.value)}
                          className={`col-span-2 px-3 py-2 ${campo} rounded-lg text-sm focus:outline-none`}
                        />
                      )}
                      {signer.authType === 'icp_brasil' && (
                        <p className="col-span-2 text-xs text-slate-500">
                          O signatario precisara ter certificado digital (e-CPF) instalado no dispositivo para assinar.
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
        </CascaDeModal>
      )}
    </div>
  );
}
